import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { planScript } from "./prosody";

export const TOKENKU_VOICE = { name: "TokenKu Voice", pitch: 1.15, rate: 0.82, volume: 1 };

export const TTS_LANGS = [
  { code: "en-US", label: "EN", name: "English" },
  { code: "id-ID", label: "ID", name: "Indonesia" },
];

export const ttsSupported =
  typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;

const MALE = /(\bmale\b|david|mark|guy|daniel|alex|fred|george|james|aaron|arthur|ryan|eric|christopher|roger|andrew|brian|tom|ardi|andika)/i;
const FEMALE = /(female|zira|samantha|victoria|karen|susan|gadis|damayanti|aria|jenny|hazel|michelle|emma|ava|allison|google us english)/i;
const norm = (l = "") => l.replace("_", "-").toLowerCase();

const pickVoice = (voices, lang) => {
  const pool = voices.filter((v) => norm(v.lang).startsWith(lang.slice(0, 2).toLowerCase()));
  const ranked = [...pool.filter((v) => norm(v.lang) === lang.toLowerCase()), ...pool];
  return ranked.find((v) => MALE.test(v.name)) || ranked.find((v) => !FEMALE.test(v.name)) || ranked[0] || null;
};

export function useTTS({ index, running, connected, lang, getLines, onFinished }) {
  const [enabled, setEnabled] = useState(true);
  const [voices, setVoices] = useState([]);
  const [line, setLine] = useState(-1);
  const [blocked, setBlocked] = useState(false);
  const gen = useRef(0);
  const pending = useRef(false);
  const gapTimer = useRef(null);
  const held = useRef(null);
  const runningRef = useRef(running);
  const cb = useRef({ getLines, onFinished });
  runningRef.current = running;
  cb.current = { getLines, onFinished };

  useEffect(() => {
    if (!ttsSupported) return undefined;
    const s = window.speechSynthesis;
    const load = () => setVoices(s.getVoices());
    load();
    s.addEventListener("voiceschanged", load);
    return () => {
      s.removeEventListener("voiceschanged", load);
      s.cancel();
    };
  }, []);

  const voice = useMemo(() => pickVoice(voices, lang), [voices, lang]);

  const stop = useCallback(() => {
    if (!ttsSupported) return;
    gen.current += 1;
    pending.current = false;
    held.current = null;
    clearTimeout(gapTimer.current);
    window.speechSynthesis.cancel();
    setLine(-1);
  }, []);

  const speak = useCallback(() => {
    if (!ttsSupported) return;
    stop();
    const s = window.speechSynthesis;
    s.resume();
    const g = gen.current;
    const segs = planScript(cb.current.getLines().filter((l) => l && l.trim()));

    const after = (ms, fn) => {
      gapTimer.current = setTimeout(() => {
        if (gen.current !== g) return;
        if (runningRef.current) fn();
        else held.current = fn;
      }, ms);
    };

    const say = (k) => {
      if (gen.current !== g) return;
      if (k >= segs.length) {
        setLine(-1);
        cb.current.onFinished();
        return;
      }
      const seg = segs[k];
      const u = new SpeechSynthesisUtterance(seg.text);
      u.lang = lang;
      if (voice) u.voice = voice;
      u.pitch = seg.pitch;
      u.rate = seg.rate;
      u.volume = seg.volume;
      let done = false;
      const finish = () => {
        if (done || gen.current !== g) return;
        done = true;
        const next = segs[k + 1];
        const gap = seg.pauseAfter + (next?.pauseBefore || 0);
        if (gap > 0) after(gap, () => say(k + 1));
        else say(k + 1);
      };
      u.onstart = () => {
        if (gen.current !== g) return;
        setLine(seg.line);
        setBlocked(false);
      };
      u.onerror = (e) => {
        if (gen.current !== g) return;
        if (e.error === "not-allowed") {
          done = true;
          setBlocked(true);
          pending.current = true;
        } else if (e.error !== "interrupted" && e.error !== "canceled") finish();
      };
      u.onend = finish;
      s.speak(u);
    };

    say(0);
  }, [lang, voice, stop]);

  const restart = useCallback(() => {
    if (!enabled) return;
    if (runningRef.current) speak();
    else {
      stop();
      pending.current = true;
    }
  }, [enabled, speak, stop]);

  useEffect(() => {
    if (!ttsSupported || !connected) return;
    if (!enabled) stop();
    else restart();
  }, [index, enabled, connected, restart, stop]);

  useEffect(() => {
    if (!ttsSupported || !enabled) return;
    const s = window.speechSynthesis;
    if (running) {
      if (pending.current) {
        pending.current = false;
        speak();
      } else if (held.current) {
        const fn = held.current;
        held.current = null;
        fn();
      } else if (s.paused) s.resume();
    } else s.pause();
  }, [running, enabled, speak]);

  const unlock = useCallback(() => {
    setBlocked(false);
    speak();
  }, [speak]);

  return { enabled, setEnabled, lang, voice, line, blocked, unlock, stop, restart };
}
